﻿import { Plugin } from 'ckeditor5';
import { Dialog, ButtonView, IframeView, Image, InputTextView } from 'ckeditor5';

const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
    <g transform="matrix(2,0,0,2,0,0)">
        <g>
            <path d="M24,8.41a1,1,0,0,0-.29-.7L18.29,2.29a1,1,0,0,0-.7-.29H2A2,2,0,0,0,0,4V20a2,2,0,0,0,2,2H22a2,2,0,0,0,2-2ZM22,19.5a.5.5,0,0,1-.5.5H2.5a.5.5,0,0,1-.5-.5V4.5A.5.5,0,0,1,2.5,4H17a.49.49,0,0,1,.36.15l4.53,4.53A.49.49,0,0,1,22,9Z"></path>
            <circle cx="8.3" cy="8" r="2"></circle>
            <path d="M13.72,10.68a.49.49,0,0,0-.84,0l-2.43,3.88a.27.27,0,0,1-.21.12.24.24,0,0,1-.21-.12l-.87-1.38a.49.49,0,0,0-.42-.24.51.51,0,0,0-.43.24L5.54,17.62a.25.25,0,0,0,0,.25.26.26,0,0,0,.22.13h12.1a.26.26,0,0,0,.22-.13.25.25,0,0,0,0-.25Z"></path>
        </g>
    </g>
</svg>`;

export default class MdbImage extends Plugin {
	static get pluginName() { return "MdbImage" };

	get requires() {
		return [Dialog, IframeView, Image, 'ImageUtils'];
	}

	imageDraggedOutSide = false;

	init() {
		const editor = this.editor;
		const dialog = editor.plugins.get('Dialog');
		const { t } = editor.locale;

		_registerConversions(editor);
		this._handleDragEnd();
		editor.ui.componentFactory.add('mdbImage', locale => {
			const mdbImageButton = new ButtonView(locale);
			mdbImageButton.set({
				label: t('insertMdbImage'),
				withText: false,
				icon: svg,
				tooltip: t('insertMdbImage')
			});

			mdbImageButton.on('execute', () => {
				const frameView = new IframeView(locale);
				frameView.set({
					label: t('insertMdbImage'),
					tooltip: true,
					withText: false
				});

				frameView.render();

				frameView.element.style.width = '950px';
				frameView.element.style.height = '640px';
				frameView.element.style.margin = '5px 15px';

				frameView.element.removeAttribute('sandbox');
				frameView.element.src = '/Dynamix/Modules/MediaDatabase/Popup.aspx?ImageForEditor=true&AllowedExtensions=images&EditorId=' + editor.id;

				frameView.element.onload = () => {
					_doResize(50, frameView.element, dialog.view.element);
				};

				function blockBlur(eventInfo) {
					eventInfo.stop();
				}

				editor.on('blur', blockBlur, { priority: 'highest' });

				function insertImage(evt) {
					editor.model.change(writer => {
						var imageElement = writer.createElement('imageInline', {
							'src': evt.detail.url,
							'alt': evt.detail.name,
							'width': evt.detail.width + 'px',
							'height': evt.detail.height + 'px',
						});

						editor.model.insertObject(imageElement, null, null, {
							setSelection: 'on'
						});
					});

					dialog.hide();
					editor.editing.view.focus();
				}

				window.addEventListener('MdbImagePlugin_InsertImage', insertImage);

				let focusElement = new InputTextView();
				focusElement.set({ tabIndex: -1, withText: false, id: 'hidden-focus-element' });

				dialog.show({
					isModal: true,
					title: t('insertMdbImage'),
					content: [
						focusElement,
						frameView
					],
					className: 'placeholder-chooser-dialog',
					onHide() {
						editor.off('blur', blockBlur);
						window.removeEventListener('MdbImagePlugin_InsertImage', insertImage);
					}
				});
			});

			return mdbImageButton;
		});
	}

	_handleDragEnd() {
		const viewDocument = this.editor.editing.view.document;
		this.listenTo(viewDocument, 'dragleave', () => {
			this.imageDraggedOutSide = true;
		}, { priority: 'high' });

		this.listenTo(viewDocument, 'dragenter', () => {
			this.imageDraggedOutSide = false;
		}, { priority: 'high' });

		this.listenTo(viewDocument, 'dragend', (evt, data) => {
			if (this.imageDraggedOutSide) {
				evt.stop();
			}
		}, { priority: 'high' });
	}
};

function _registerConversions(editor) {
	const imageUtils = editor.plugins.get('ImageUtils');

	editor.conversion.for('upcast')
		.elementToElement({
			view: 'img',
			model: (viewElement, { writer }) => {
				let element = writer.createElement('imageInline', {
					src: viewElement.getAttribute('src'),
					alt: viewElement.getAttribute('alt'),
				});

				let imageAlign = viewElement.getAttribute('align');
				if (imageAlign === 'left') {
					writer.setAttribute('imageStyle', 'alignLeft', element);
				}
				else if (imageAlign === 'right') {
					writer.setAttribute('imageStyle', 'alignRight', element);
				}

				// get original image size
				let image = document.createElement('img');
				image.src = viewElement.getAttribute('src');

				if (image.width && image.width !== 0) { //If the image is cached (should usually be the case) we can get the width immediately
					_setImageUpcastValues(viewElement, image, writer, element);
				}
				else { //If the image is not cached (or the browser does not like us) we wait for the onload to set the values
					image.onload = function () {
						editor.model.change(onChangeWriter => {
							_setImageUpcastValues(viewElement, image, onChangeWriter, element);
						})
					};
				}

				return element;

			},
			converterPriority: 'highest'
		});

	editor.conversion.for('dataDowncast')
		.elementToElement({
			model: 'imageInline',
			view: (modelElement, { writer }) => {
				let imgWidth = modelElement.getAttribute('resizedWidth') || modelElement.getAttribute('originalWidth');
				let imgHeight = modelElement.getAttribute('resizedHeight') || modelElement.getAttribute('originalHeight');

				let emptyElementData = {
					src: modelElement.getAttribute('src'),
					alt: modelElement.getAttribute('alt')
				};

				if (imgWidth) {
					emptyElementData.width = imgWidth;
					emptyElementData.height = imgHeight;
				}

				let img = writer.createEmptyElement('img', emptyElementData);

				if (imgWidth) {
					writer.setStyle('width', `${_convertNumber(imgWidth)}px`, img);
					writer.setStyle('height', `${_convertNumber(imgHeight)}px`, img);
				}

				let imageAlign = modelElement.getAttribute('imageStyle');
				if (imageAlign === 'alignLeft') {
					writer.setAttribute('align', 'left', img);
					writer.setStyle('float', 'left', img);
				}
				else if (imageAlign === 'alignRight') {
					writer.setAttribute('align', 'right', img);
					writer.setStyle('float', 'right', img);
				}
				else {
					writer.removeAttribute('align', img);
					writer.removeStyle('float', img);
				}

				return img;
			},
			converterPriority: 'highest'
		})
		.add(dispatcher => {
			dispatcher.on('attribute:imageStyle', (evt, data, conversionApi) => {
				const viewWriter = conversionApi.writer;
				const viewElement = conversionApi.mapper.toViewElement(data.item);
				const img = imageUtils.findViewImgElement(viewElement);

				// remove image-style classes when generating output
				viewWriter.removeClass('image-style-align-left', img);
				viewWriter.removeClass('image-style-align-right', img);

				evt.stop();
			});
		});

	editor.conversion.for('downcast').add(dispatcher => {
		dispatcher.on('attribute:height:imageInline', (evt, data, conversionApi) => {
			// stop event handling to avoid setting aspect-ratio style
			evt.stop();
		});
		dispatcher.on('attribute:width:imageInline', (evt, data, conversionApi) => {
			// stop event handling to avoid setting aspect-ratio style
			evt.stop();
		});

		dispatcher.on('attribute:resizedWidth:imageInline', (evt, data, conversionApi) => {
			const viewWriter = conversionApi.writer;
			const viewElement = conversionApi.mapper.toViewElement(data.item);
			const img = imageUtils.findViewImgElement(viewElement);

			// stop further event processing
			evt.stop();

			viewWriter.removeClass('image_resized', img);
			viewWriter.removeStyle('aspect-ratio', img);

			if (data.attributeNewValue === null || data.attributeNewValue === undefined) {
				viewWriter.removeStyle("width", img);
				viewWriter.removeStyle("height", img);
				viewWriter.removeAttribute("width", img);
				viewWriter.removeAttribute("height", img);
			}
			else {
				let ratio = data.item.getAttribute('aspectRatio');
				if (ratio === null || ratio === undefined) {
					ratio = _convertNumber(data.item.getAttribute('width')) / _convertNumber(data.item.getAttribute('height'));
				}

				let width = Math.round(_convertNumber(data.attributeNewValue));
				let height = Math.round(width / ratio);

				viewWriter.setStyle('width', `${width}px`, img);
				viewWriter.setStyle('height', `${height}px`, img);
				viewWriter.setAttribute('width', width, img);
				viewWriter.setAttribute('height', height, img);
			}
		});
	});
}

function _setImageUpcastValues(viewElement, image, writer, element) {
	let originalWidth = image.width,
		originalHeight = image.height;

	// preserve original size information for later processing
	writer.setAttribute('originalWidth', originalWidth, element);
	writer.setAttribute('originalHeight', originalHeight, element);
	writer.setAttribute('aspectRatio', (originalWidth / originalHeight), element);

	let resizedWidth = _convertNumber(viewElement.getAttribute('width')),
		resizedHeight = _convertNumber(viewElement.getAttribute('height'));

	if (!resizedWidth) {
		resizedWidth = originalWidth;
		resizedHeight = originalHeight;
	}
	else if (!resizedHeight) {
		resizedHeight = originalHeight * (resizedWidth / originalWidth);
	}

	writer.setAttribute('width', originalWidth, element);
	writer.setAttribute('height', originalHeight, element);
	if (resizedWidth !== originalWidth) {
		writer.setAttribute('resizedWidth', `${resizedWidth}px`, element);
		writer.setAttribute('resizedHeight', `${resizedHeight}px`, element);
	}
	else {
		writer.setAttribute('resizedWidth', null, element);
		writer.setAttribute('resizedHeight', null, element);
	}
}

function _convertNumber(text) {
	if (text != null && text != undefined) {
		return parseFloat(text);
	}

	return text;
}
function _doResize(dialogBorder, mdbView, dialogView) {
	let width = dialogView.offsetWidth - (dialogBorder * 2);
	let height = dialogView.offsetHeight - (dialogBorder * 2);

	mdbView.style.width = width + 'px';
	mdbView.style.height = height + 'px';
	dialogView.children[0].style.left = dialogBorder + 'px';
	dialogView.children[0].style.top = dialogBorder + 'px';
}